import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageShell } from '@/components/layout/page-shell';
import { ContentCard } from '@/components/feature/content-card';
import { useContentFeed } from '@/hooks/use-content';
import { cls } from '@/lib/format';
import type { ContentFeedParams, ContentType } from '@/types/content';
import { contentTypeEmojis, contentTypeLabels } from '@/types/content';

// SPEC-021: Learning Hub feed. Filter chips for type + bookmarked, then
// a list of content cards. Row click navigates into the viewer route.

type TypeFilter = ContentType | 'all';

const TYPE_ORDER: TypeFilter[] = ['all', 'article', 'audio', 'video', 'mixed'];

export default function LearningHubPage() {
  const navigate = useNavigate();
  const [type, setType] = useState<TypeFilter>('all');
  const [bookmarked, setBookmarked] = useState(false);

  const params: ContentFeedParams = {
    type: type === 'all' ? undefined : type,
    bookmarked: bookmarked || undefined,
  };
  const { data, isLoading } = useContentFeed(params);
  const items = data ?? [];

  return (
    <PageShell title="Learning Hub">
      <div className="sticky top-0 z-10 bg-[var(--color-bg)] px-4 pb-2 pt-1">
        <div className="flex gap-1 overflow-x-auto rounded-xl bg-[var(--color-secondary-bg)] p-1">
          {TYPE_ORDER.map((t) => {
            const active = type === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={cls(
                  'flex shrink-0 items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-opacity',
                  active
                    ? 'bg-[var(--color-bg)] text-[var(--color-fg)] shadow-sm'
                    : 'text-[var(--color-muted)]',
                )}
              >
                {t !== 'all' && <span>{contentTypeEmojis[t]}</span>}
                <span>{t === 'all' ? 'All' : contentTypeLabels[t]}</span>
              </button>
            );
          })}
        </div>
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={() => setBookmarked((v) => !v)}
            className={cls(
              'rounded-full border px-3 py-1 text-xs transition-colors',
              bookmarked
                ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-white'
                : 'border-[var(--color-separator)] text-[var(--color-muted)]',
            )}
          >
            {bookmarked ? '🔖 Saved only' : '📑 Show saved only'}
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-2 px-4 py-2">
        {isLoading ? (
          <p className="py-10 text-center text-sm text-[var(--color-muted)]">Loading…</p>
        ) : items.length === 0 ? (
          <p className="rounded-xl bg-[var(--color-secondary-bg)] px-4 py-8 text-center text-sm text-[var(--color-muted)]">
            {bookmarked
              ? 'No saved content yet. Tap the bookmark on a piece you like.'
              : 'No content visible to you yet. Your teacher can publish new pieces from the dashboard.'}
          </p>
        ) : (
          items.map((item) => (
            <ContentCard
              key={item.id}
              item={item}
              onClick={() => navigate(`/learn/${item.id}`)}
            />
          ))
        )}
      </div>
    </PageShell>
  );
}
